import { Group, Select, Stack, Table, Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	auditKeys,
	getAdminAuditFilters,
	listAdminAudit,
} from "@/features/audit/api";
import type { ListAdminAuditParams } from "@/features/audit/types";
import type { PageLimit } from "@/shared/api/types";
import { JournalShell } from "@/shared/ui/journal-shell";

function actionLabel(
	t: (key: string, options?: { defaultValue?: string }) => string,
	action: string,
): string {
	return t(`audit.actions.${action.replaceAll(".", "_")}`, {
		defaultValue: action,
	});
}

export function AuditLogPage() {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const [debouncedSearch] = useDebouncedValue(search, 300);
	const [actorId, setActorId] = useState<string | null>(null);
	const [action, setAction] = useState<string | null>(null);
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState<PageLimit>(20);

	const params: ListAdminAuditParams = useMemo(
		() => ({
			search:
				debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : undefined,
			actorId: actorId ?? undefined,
			action: action ?? undefined,
			from: from || undefined,
			to: to || undefined,
			page,
			limit,
			sort: "createdAt",
			order: "DESC",
		}),
		[debouncedSearch, actorId, action, from, to, page, limit],
	);

	const filtersQuery = useQuery({
		queryKey: auditKeys.filters,
		queryFn: getAdminAuditFilters,
	});

	const listQuery = useQuery({
		queryKey: auditKeys.list(params),
		queryFn: () => listAdminAudit(params),
	});

	return (
		<JournalShell
			title={t("audit.title")}
			subtitle={t("audit.subtitle")}
			isLoading={listQuery.isLoading}
			error={listQuery.error}
			errorTitle={t("audit.loadError")}
			onRetry={() => void listQuery.refetch()}
			empty={t("audit.empty")}
			hasRows={Boolean(listQuery.data && listQuery.data.data.length > 0)}
			page={page}
			total={listQuery.data?.totalItemsCount ?? 0}
			hasNextPage={Boolean(listQuery.data?.hasNextPage)}
			isFetching={listQuery.isFetching}
			onPageChange={setPage}
			filters={
				<Stack gap="md">
					<Group grow preventGrowOverflow={false} wrap="wrap">
						<TextInput
							label={t("common.search")}
							placeholder={t("audit.searchHint")}
							value={search}
							onChange={(event) => {
								setSearch(event.currentTarget.value);
								setPage(1);
							}}
						/>
						<Select
							label={t("audit.admin")}
							clearable
							searchable
							data={(filtersQuery.data?.actors ?? []).map((row) => ({
								value: row.id,
								label: `${row.fullName} · ${row.email}`,
							}))}
							value={actorId}
							onChange={(value) => {
								setActorId(value);
								setPage(1);
							}}
						/>
						<Select
							label={t("audit.action")}
							clearable
							searchable
							data={(filtersQuery.data?.actions ?? []).map((value) => ({
								value,
								label: actionLabel(t, value),
							}))}
							value={action}
							onChange={(value) => {
								setAction(value);
								setPage(1);
							}}
						/>
					</Group>
					<Group grow preventGrowOverflow={false} wrap="wrap" maw={560}>
						<TextInput
							label={t("referral.from")}
							type="date"
							value={from}
							onChange={(event) => {
								setFrom(event.currentTarget.value);
								setPage(1);
							}}
						/>
						<TextInput
							label={t("referral.to")}
							type="date"
							value={to}
							onChange={(event) => {
								setTo(event.currentTarget.value);
								setPage(1);
							}}
						/>
						<Select
							label={t("common.rows")}
							data={[
								{ value: "20", label: "20" },
								{ value: "50", label: "50" },
								{ value: "100", label: "100" },
							]}
							value={String(limit)}
							onChange={(value) => {
								if (value) {
									setLimit(Number(value) as PageLimit);
									setPage(1);
								}
							}}
						/>
					</Group>
				</Stack>
			}
		>
			<Table.ScrollContainer type="native" minWidth={920}>
				<Table verticalSpacing="sm" highlightOnHover>
					<Table.Thead>
						<Table.Tr>
							<Table.Th>{t("audit.when")}</Table.Th>
							<Table.Th>{t("audit.admin")}</Table.Th>
							<Table.Th>{t("audit.action")}</Table.Th>
							<Table.Th>{t("audit.object")}</Table.Th>
							<Table.Th>{t("audit.comment")}</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{(listQuery.data?.data ?? []).map((row) => (
							<Table.Tr key={row.id}>
								<Table.Td>
									{new Date(row.createdAt).toLocaleString("uk-UA")}
								</Table.Td>
								<Table.Td>
									{row.actor ? (
										<Stack gap={0}>
											<Text size="sm">{row.actor.fullName}</Text>
											<Text size="xs" c="dimmed">
												{row.actor.email}
											</Text>
										</Stack>
									) : (
										<Text c="dimmed">{t("common.dash")}</Text>
									)}
								</Table.Td>
								<Table.Td>{actionLabel(t, row.action)}</Table.Td>
								<Table.Td>
									<Stack gap={0}>
										<Text size="sm">{row.entityType}</Text>
										{row.entityId ? (
											<Text size="xs" c="dimmed">
												{row.entityId}
											</Text>
										) : null}
									</Stack>
								</Table.Td>
								<Table.Td>
									{row.comment ? (
										<Text size="sm">{row.comment}</Text>
									) : (
										<Text c="dimmed">{t("common.dash")}</Text>
									)}
								</Table.Td>
							</Table.Tr>
						))}
					</Table.Tbody>
				</Table>
			</Table.ScrollContainer>
		</JournalShell>
	);
}
